import WebAppAPI from '@/utils/WebAppAPI';
import { AxiosResponse } from 'axios';
import type {
  ApiResponse,
  GetAllProjectsQueryKey,
  ProjectInsights,
  ProjectQueryParams,
} from '@/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

const WebAppService = {
  signUp: async (payload: Record<string, unknown>): Promise<any> => {
    try {
      const response = await WebAppAPI().post('/auth', payload);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getOTP: async (payload: Record<string, unknown>): Promise<AxiosResponse> => {
    return await WebAppAPI().post('/otp', payload);
  },

  verifyAndLogin: async (payload: Record<string, unknown>): Promise<any> => {
    try {
      const response = await WebAppAPI().post('/otp/validate', payload);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getCurrentUser: async (id: string): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/users/${id}`);
      return response?.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  getAdminsInOrg: async (organizationId: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`/users/${organizationId}/admins`);
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  getMembersInOrg: async (
    organizationId: string,
    page: number = 1,
    limit: number = 5,
    search: string = ''
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(
        `/organizations/${organizationId}/members`,
        {
          params: {
            page,
            limit,
            search,
          },
        }
      );
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  lookupOrgMember: async (
    organizationId: string,
    email: string
  ): Promise<any> => {
    const { data } = await WebAppAPI().get(
      `/organizations/${organizationId}/members/lookup`,
      { params: { email } }
    );
    return data;
  },

  makeAdmin: async (email: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().patch(`/users/make-admin`, {
        email: email,
      });
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  demoteAdmin: async (email: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().patch(`/users/demote-admin`, {
        email: email,
      });
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  updateProfile: async (
    userId: string,
    payload: { first_name: string; last_name: string }
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().patch(
        `/users/${userId}/update-profile`,
        payload
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getLegacySingleProject: async (
    organization_id: string,
    project_id: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/organizations/${organization_id}/project/${project_id}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  },

  getProjectDetails: async (projectId: string): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/tasks/project-tasks/${projectId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  },

  getOrganizationById: async (organizationId: string): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/organizations/${organizationId}`,
        {
          params: {
            id: organizationId,
          },
        }
      );
      return response?.data?.data;
    } catch (error) {
      throw error;
    }
  },

  getOrganizationJiraKey: async (organizationId: string): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/organizations/${organizationId}/projects?id=${organizationId}`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  configureJira: async (
    organizationId: string,
    formData: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(
        `/organizations/${organizationId}`,
        formData
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  saveEditedProject: async (
    projectId: string,
    updatedProject: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(
        `/projects/${projectId}`,
        updatedProject
      );
      return response?.data;
    } catch (error) {
      console.error('Error saving project', error);
      throw error;
    }
  },

  updateFlag: async (userId: string): Promise<any> => {
    try {
      const response = await WebAppAPI().patch(`users/${userId}/update-flag`);
      return response?.data;
    } catch (error) {
      console.error(`Failed to update flag`, error);
      throw error;
    }
  },

  getAllPlans: async (): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/subscription-plans`);
      return response?.data;
    } catch (error) {
      console.error('Failed to get plans');
      throw error;
    }
  },

  createCheckoutSession: async (
    planId: Record<string, unknown>,
    userId: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/payments/create-checkout-session`,
        planId,
        userId
      );
      return response?.data;
    } catch (error) {
      console.error('Failed to create checkout session');
      throw error;
    }
  },

  getCurrentSubscription: async (): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/subscriptions/current`);
      return response?.data;
    } catch (error) {
      console.error('Failed to get subscription plan');
      throw error;
    }
  },

  getOrganizationJiraProjects: async (
    organizationId: string,
    startPageAt: number = 0
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/organizations/${organizationId}/projects?startPageAt=${startPageAt}`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getOrganizationVisibleJiraProjects: async (
    organizationId: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/organizations/${organizationId}/projects/visibleJiraProjects`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  allowJiraVisibility: async (
    organizationId: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(
        `/organizations/${organizationId}/projects/allowJiraVisibility`,
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  removeJiraVisibility: async (
    organizationId: string,
    projectKey: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().delete(
        `/organizations/${organizationId}/projects/removeJiraVisibility/${projectKey}`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getJiraProjectEpics: async (project_key: string): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/reports/${project_key}/epics`);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getChildrenOfAnEpic: async (
    projectKey: string,
    epicId: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/reports/${projectKey}/epic/${epicId}`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  configureOpenAI: async (
    organizationId: string,
    formData: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(
        `/organizations/${organizationId}/openai-key`,
        formData
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteOpenAI: async (organizationId: string): Promise<any> => {
    try {
      const response = await WebAppAPI().delete(
        `/organizations/${organizationId}/openai-key`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteTaskById: async (taskId: string): Promise<any> => {
    try {
      const response = await WebAppAPI().delete(`/tasks/${taskId}`);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  updateTaskById: async (
    taskId: string,
    data: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(`/tasks/update/${taskId}`, data);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  checkOrganizationCredits: async (): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/check_credits/status`);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  // ========== Slack Integration ==========

  installSlack: async (): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/slack/install-link`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uninstallSlack: async (): Promise<any> => {
    try {
      const response = await WebAppAPI().delete(
        `/organizations/delete-slack-integration`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============== Create team ===============

  createTeam: async (payload: Record<string, unknown>): Promise<any> => {
    try {
      const response = await WebAppAPI().post(`/teams`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTeamOverview: async (team_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`/teams/${team_id}/overview`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  getTeamMembersMetrics: async (team_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(
        `/teams/${team_id}/members/metrics`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getTeamsYouOwn: async (params?: Record<string, unknown>): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/teams/user-owned`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTeamsAUserIsPartOf: async (
    params?: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/teams/user-teams`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSingleTeam: async (team_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().delete(`/teams/${team_id}`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  getMembersInATeam: async (team_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`/teams/${team_id}/users`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  transferTeamOwnership: async (
    team_id: string,
    email: string
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().put(`/teams/${team_id}/update-owner`, {
        email,
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  updateTeam: async (
    team_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().put(`/teams/${team_id}`, payload);
      return data;
    } catch (error) {
      throw error;
    }
  },

  addUsersToTeam: async (
    team_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/teams/${team_id}/add-users`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  removeMemberFromTeam: async (
    team_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().delete(
        `/teams/${team_id}/delete-user`,
        { data: payload }
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  leaveTeam: async (
    team_id: string,
    email: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().delete(`/teams/${team_id}/leave`, {
        data: email,
      });
      console.log(data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  getTeamHealth: async (team_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`/teams/${team_id}/health`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  // ========== projects ==================

  getOrgTeams: async (): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/teams`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createNewProject: async (
    org_id: string,
    payload: FormData | Record<string, unknown>
  ): Promise<any> => {
    try {
      const res = await WebAppAPI().post(
        `/organizations/${org_id}/project/create_project`,
        payload
      );
      console.log(res.data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  createProjectFromFiles: async (
    org_id: string,
    payload: FormData
  ): Promise<any> => {
    try {
      const res = await WebAppAPI().post(
        `/organizations/${org_id}/project/create_project_from_files`,
        payload
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateProject: async (
    org_id: string,
    project_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const res = await WebAppAPI().put(
        `/organizations/${org_id}/project/${project_id}`,
        payload
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getProjects: async (
    org_id: string,
    params: ProjectQueryParams = {}
  ): Promise<any> => {
    try {
      const queryParams: Record<string, string> = {
        page: params.page?.toString() || '1',
        limit: params.limit?.toString() || '10',
        sort_by: params.sort_by || 'created_at',
      };

      if (params.status) {
        queryParams.status = params.status;
      }

      if (params.priority) {
        queryParams.priority = params.priority;
      }

      if (params.search) {
        queryParams.search = params.search;
        // Some backend endpoints expect a `name` query param for project name filtering.
        // Include `name` alongside `search` to support both conventions.
        queryParams.name = params.name || params.search;
      }

      if (params.name && !queryParams.name) {
        queryParams.name = params.name as string;
      }

      if (params.order) {
        queryParams.order = params.order;
      }

      const query = new URLSearchParams(queryParams);

      const res = await WebAppAPI().get(
        `organizations/${org_id}/project?${query.toString()}`
      );

      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getSingleProject: async (
    org_id: string,
    project_id: string
  ): Promise<any> => {
    try {
      const res = await WebAppAPI().get(
        `organizations/${org_id}/project/${project_id}`
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  transferProjectOwnership: async (
    organizationId: string,
    projectId: string,
    project_owner: string
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().put(
        `/organizations/${organizationId}/project/${projectId}/owner`,
        { project_owner }
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getProjectInsights: async (
    org_id: string,
    project_id: string
  ): Promise<AxiosResponse> => {
    try {
      const { data } = await WebAppAPI().get(
        `organizations/${org_id}/project/${project_id}/project-insights`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },
  refreshProjectFromJira: async (projectId: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().post(
        `/projects/${projectId}/refresh-from-jira`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },
  getTasksForAProject: async (project_id: string): Promise<any> => {
    try {
      const res = await WebAppAPI().get(`tasks/${project_id}`);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSingleProject: async (
    org_id: string,
    project_id: string
  ): Promise<any> => {
    try {
      const res = await WebAppAPI().delete(
        `/organizations/${org_id}/project/${project_id}`
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  getAllProjects: async ({
    queryKey,
  }: {
    queryKey: [string, GetAllProjectsQueryKey];
  }): Promise<any> => {
    const [, { page, searchQuery, limit, organization_id }] = queryKey;

    try {
      const response = await WebAppAPI().get(
        `/organizations/${organization_id}/project`,
        {
          params: {
            page: page || 1,
            search: searchQuery || '',
            limit: limit || 10,
          },
        }
      );

      return response?.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  createTask: async (
    payload: Record<string, unknown>,
    project_id: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/tasks/create-project-task/${project_id}`,
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getAllTasks: async ({ project_id }: { project_id: string }): Promise<any> => {
    try {
      const response = await WebAppAPI().get(`/tasks/${project_id}`);
      return response?.data.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  updateProjectStatus: async (
    org_id: string,
    project_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(
        `/organizations/${org_id}/project/${project_id}/status`,
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getUserTasksGroupedByProject: async (email: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`/tasks/user-tasks/${email}`);
      return data.data;
    } catch (error) {
      throw error;
    }
  },

  getUserTasksByProjects: async (email: string, page = 1): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(
        `/tasks/user-tasks/${email}?page=${page}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getUserTasksByIntakes: async (email: string, page = 1): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(
        `/tasks/user-tasks/${email}/intakes?page=${page}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getTasksBoard: async ({
    project_id,
  }: {
    project_id: string;
  }): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/tasks/project-board/${project_id}`
      );
      return response?.data.data;
    } catch (error) {
      console.error('Error tasks board:', error);
      throw error;
    }
  },

  getTeamWorkload: async (
    org_id: string,
    project_id: string,
    source?: 'Jira'
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/organizations/${org_id}/project/${project_id}/team-workload`,
        { params: { source } }
      );
      return response?.data;
    } catch (error) {
      console.error('Error fetching team workload:', error);
      throw error;
    }
  },

  // ======== project links =========

  createProjectLink: async (
    project_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/projects/${project_id}/project_link`,
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getProjectLinks: async (project_id: string): Promise<any> => {
    try {
      const response = await WebAppAPI().get(
        `/projects/${project_id}/project_link`
      );
      return response?.data.data;
    } catch (error) {
      console.error('Error fetching project links:', error);
      throw error;
    }
  },

  updateProjectLink: async (
    project_id: string,
    link_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().put(
        `/projects/${project_id}/project_link/${link_id}`,
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteProjectLink: async (
    project_id: string,
    link_id: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().delete(
        `/projects/${project_id}/project_link/${link_id}`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  assignMemberToTask: async (
    task_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().post(
        `/tasks/${task_id}/assign`,
        payload
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  unassignMemberFromTask: async (task_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().put(`/tasks/${task_id}/unassign`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  getTaskDetails: async (task_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`tasks/${task_id}`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  // ============= task metadata =============

  // ======= task links
  getTaskLinks: async (task_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`tasks/${task_id}/task_link`);
      return data;
    } catch (error) {
      throw error;
    }
  },

  createTaskLink: async (
    task_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().post(
        `tasks/${task_id}/task_link`,
        payload
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  deleteTaskLink: async (task_id: string, link_id: string): Promise<any> => {
    try {
      const { data } = await WebAppAPI().delete(
        `tasks/${task_id}/task_link/${link_id}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  // ======= task comments

  getTaskComments: async (
    task_id: string,
    params?: { page?: number; limit?: number }
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(`tasks/${task_id}/task_comment`, {
        params,
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  createTaskComment: async (
    task_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().post(
        `tasks/${task_id}/task_comment`,
        payload
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  updateTaskComment: async (
    task_id: string,
    comment_id: string,
    payload: Record<string, unknown>
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().put(
        `tasks/${task_id}/task_comment/${comment_id}`,
        payload
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  deleteTaskComment: async (
    task_id: string,
    comment_id: string
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().delete(
        `tasks/${task_id}/task_comment/${comment_id}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  // =====================================
  // =========== legacy projects ==========

  createTicket: async (
    formData: FormData | Record<string, unknown>,
    organizationId: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/organizations/${organizationId}/file`,
        formData
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  createLegacyProject: async (
    formData: FormData | Record<string, unknown>,
    organizationId: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/organizations/${organizationId}/project/create_legacy_project`,
        formData
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  sendProjectToJira: async (
    projectId: string,
    project_key: Record<string, unknown>
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().post(
        `/legacy_projects/${projectId}`,
        project_key
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteProjectById: async (
    organization_id: string,
    projectId: string
  ): Promise<any> => {
    try {
      const response = await WebAppAPI().delete(
        `/organizations/${organization_id}/project/${projectId}`
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  // ============= Enums ===================

  // Task enums
  getTaskStatuses: async (): Promise<any> => {
    const response = await WebAppAPI().get('/tasks/enums/statuses');
    return response?.data;
  },
  getTaskTypes: async (): Promise<any> => {
    const response = await WebAppAPI().get('/tasks/enums/types');
    return response?.data;
  },
  getTaskPriorities: async (): Promise<any> => {
    const response = await WebAppAPI().get('/tasks/enums/priorities');
    return response?.data;
  },
  getTaskDepartments: async (): Promise<any> => {
    const response = await WebAppAPI().get('/tasks/enums/departments');
    return response?.data;
  },
  getTaskLinkTypes: async (): Promise<any> => {
    const response = await WebAppAPI().get('/tasks/enums/link-types');
    return response?.data;
  },

  // Project enums
  getProjectStatuses: async (): Promise<any> => {
    const response = await WebAppAPI().get('/project/enums/statuses');
    return response?.data;
  },
  getProjectPriorities: async (): Promise<any> => {
    const response = await WebAppAPI().get('/project/enums/priorities');
    return response?.data;
  },
  getProjectLinkTypes: async (): Promise<any> => {
    const response = await WebAppAPI().get('/project/enums/link-types');
    return response?.data;
  },

  // ========================================
};

export default WebAppService;
